import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Switch,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import api from "../../utils/api";

// ─── Styles ───────────────────────────────────────────────────────────────────

const paperSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  overflow: "hidden",
  backgroundColor: "rgba(255,255,255,0.96)",
  p: { xs: 2, md: 2.5 },
};

const fieldSx = {
  mt: 1.75,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.96)",
    borderRadius: "10px",
  },
};

const primaryButtonSx = {
  backgroundColor: "#2f63ad",
  color: "white",
  fontWeight: 700,
  borderRadius: "10px",
  textTransform: "none",
  px: 2.5,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1f4f94",
    boxShadow: "none",
  },
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  borderColor: "rgba(47,99,173,0.25)",
  color: "#2f63ad",
  px: 2.5,
};

const switchRowSx = {
  mt: 1.5,
  px: 1.25,
  py: 1.1,
  borderRadius: "12px",
  backgroundColor: "rgba(47,99,173,0.04)",
  border: "1px solid rgba(47,99,173,0.08)",
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SSOSettings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Google settings
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleClientSecret, setGoogleClientSecret] = useState("");

  // SAML settings
  const [samlEnabled, setSamlEnabled] = useState(false);
  const [samlEntryPoint, setSamlEntryPoint] = useState("");
  const [samlEntityId, setSamlEntityId] = useState(`${window.location.origin}/api/auth/saml/metadata`);
  const [samlCert, setSamlCert] = useState("");
  const [samlEmailAttr, setSamlEmailAttr] = useState("email");

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const closeSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  // ── Load existing config on mount ──────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    api
      .get("/sso-config")
      .then((res) => {
        const d = res.data || {};
        // Google
        setGoogleEnabled(d.google?.enabled ?? false);
        setGoogleClientId(d.google?.googleClientId ?? "");
        setGoogleClientSecret(d.google?.googleClientSecret ?? "");
        // SAML
        setSamlEnabled(d.saml?.enabled ?? false);
        setSamlEntryPoint(d.saml?.entryPoint ?? "");
        setSamlEntityId(d.saml?.entityId ?? `${window.location.origin}/api/auth/saml/metadata`);
        setSamlCert(d.saml?.cert ?? "");
        setSamlEmailAttr(d.saml?.emailAttribute ?? "email");
      })
      .catch(() => {
        showSnackbar("Failed to load SSO configuration.", "error");
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Save handlers ──────────────────────────────────────────────────────────
  const handleSaveGoogle = async () => {
    setSaving(true);
    try {
      await api.put("/sso-config/google", {
        enabled: googleEnabled,
        googleClientId,
        googleClientSecret,
      });
      showSnackbar("Google SSO settings saved.", "success");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to save Google settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSaml = async () => {
    setSaving(true);
    try {
      await api.put("/sso-config/saml", {
        enabled: samlEnabled,
        entryPoint: samlEntryPoint,
        entityId: samlEntityId,
        cert: samlCert,
        emailAttribute: samlEmailAttr,
      });
      showSnackbar("SAML SSO settings saved.", "success");
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to save SAML settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Section header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          SSO / Single Sign-On
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Configure Google and SAML single sign-on to let users log in with their organisation credentials.
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.95rem" },
          "& .Mui-selected": { color: "#2f63ad" },
          "& .MuiTabs-indicator": { backgroundColor: "#2f63ad" },
        }}
      >
        <Tab label="Google" />
        <Tab label="SAML" />
      </Tabs>

      {/* ── Google tab ──────────────────────────────────────────────────────── */}
      {activeTab === 0 && (
        <Box sx={{ display: "grid", gap: 2.5 }}>
          <Paper elevation={0} sx={paperSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Google Sign-In
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
              Allow users to sign in with their Google account via OAuth 2.0.
            </Typography>

            <Box sx={switchRowSx}>
              <FormControlLabel
                control={
                  <Switch
                    checked={googleEnabled}
                    onChange={(e) => setGoogleEnabled(e.target.checked)}
                    sx={{ "& .MuiSwitch-thumb": { color: googleEnabled ? "#2f63ad" : undefined } }}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 600, color: "#223142" }}>
                    Enable Google Sign-In
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            <TextField
              fullWidth
              label="Google Client ID"
              value={googleClientId}
              onChange={(e) => setGoogleClientId(e.target.value)}
              placeholder="e.g. 123456789-abc.apps.googleusercontent.com"
              required={googleEnabled}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              label="Google Client Secret"
              type="password"
              value={googleClientSecret}
              onChange={(e) => setGoogleClientSecret(e.target.value)}
              placeholder="Your Google OAuth client secret"
              sx={fieldSx}
            />

            <Accordion
              elevation={0}
              sx={{
                mt: 2.5,
                border: "1px solid rgba(47,99,173,0.12)",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#2f63ad" }} />}>
                <Typography sx={{ fontWeight: 600, color: "#2f63ad", fontSize: "0.9rem" }}>
                  Setup Instructions. Google Cloud Console
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box component="ol" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {[
                    'Go to console.cloud.google.com and open (or create) your project.',
                    'Navigate to APIs & Services → Credentials.',
                    'Click Create Credentials → OAuth 2.0 Client ID.',
                    'Set Application type to "Web application".',
                    `Add ${window.location.origin} to Authorised JavaScript origins.`,
                    `Add ${window.location.origin}/api/auth/google/callback to Authorised redirect URIs.`,
                    'Copy the Client ID and Client Secret into the fields above.',
                    'Make sure the Google+ API (or People API) is enabled for the project.',
                  ].map((step, i) => (
                    <Typography key={i} component="li" variant="body2" sx={{ color: "#3a4a5c", lineHeight: 1.6 }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} sx={{ color: "white" }} /> : <SaveIcon />}
                onClick={handleSaveGoogle}
                disabled={saving}
                sx={primaryButtonSx}
              >
                {saving ? "Saving…" : "Save Google Settings"}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ── SAML tab ────────────────────────────────────────────────────────── */}
      {activeTab === 1 && (
        <Box sx={{ display: "grid", gap: 2.5 }}>
          <Paper elevation={0} sx={paperSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              SAML Single Sign-On
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
              Integrate with any SAML 2.0 identity provider (Okta, Azure AD, OneLogin, Google Workspace, etc.).
            </Typography>

            <Box sx={switchRowSx}>
              <FormControlLabel
                control={
                  <Switch
                    checked={samlEnabled}
                    onChange={(e) => setSamlEnabled(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 600, color: "#223142" }}>
                    Enable SAML SSO
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            <TextField
              fullWidth
              label="IdP Entry Point URL (SSO URL)"
              value={samlEntryPoint}
              onChange={(e) => setSamlEntryPoint(e.target.value)}
              placeholder="https://your-idp.example.com/sso/saml"
              helperText="The SAML 2.0 SSO URL provided by your identity provider."
              sx={fieldSx}
            />

            <TextField
              fullWidth
              label="SP Entity ID / Issuer"
              value={samlEntityId}
              onChange={(e) => setSamlEntityId(e.target.value)}
              helperText="This is your service provider's unique identifier. Register this value in your IdP."
              sx={fieldSx}
            />

            <TextField
              fullWidth
              multiline
              minRows={5}
              label="IdP Certificate (PEM)"
              value={samlCert}
              onChange={(e) => setSamlCert(e.target.value)}
              placeholder={"-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
              helperText="The X.509 signing certificate from your identity provider. Paste the full PEM text including the header/footer lines."
              sx={fieldSx}
              inputProps={{ style: { fontFamily: "monospace", fontSize: "0.82rem" } }}
            />

            <TextField
              fullWidth
              label="Email Attribute Name"
              value={samlEmailAttr}
              onChange={(e) => setSamlEmailAttr(e.target.value)}
              helperText='The SAML attribute that contains the user email address. Common values: "email", "emailAddress", "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress".'
              sx={fieldSx}
            />

            <Box sx={{ mt: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open("/api/auth/saml/metadata", "_blank")}
                sx={secondaryButtonSx}
              >
                Download SP Metadata
              </Button>
            </Box>

            <Accordion
              elevation={0}
              sx={{
                mt: 2.5,
                border: "1px solid rgba(47,99,173,0.12)",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#2f63ad" }} />}>
                <Typography sx={{ fontWeight: 600, color: "#2f63ad", fontSize: "0.9rem" }}>
                  Setup Instructions. Identity Provider Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box component="ol" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {[
                    'In your identity provider (Okta, Azure AD, OneLogin, etc.), create a new SAML 2.0 application.',
                    `Set the ACS (Assertion Consumer Service) URL to: ${window.location.origin}/api/auth/saml/callback`,
                    `Set the SP Entity ID / Audience URI to the value shown in the "SP Entity ID / Issuer" field above.`,
                    'Configure the IdP to include the user email in a SAML attribute and note the attribute name.',
                    'Copy the IdP SSO URL into the "IdP Entry Point URL" field above.',
                    'Download the IdP X.509 signing certificate and paste its PEM content into the "IdP Certificate" field above.',
                    'Optionally click "Download SP Metadata" to import this service provider into your IdP automatically.',
                    'Save settings here, then test by clicking "Sign in with SSO" on the login page.',
                  ].map((step, i) => (
                    <Typography key={i} component="li" variant="body2" sx={{ color: "#3a4a5c", lineHeight: 1.6 }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} sx={{ color: "white" }} /> : <SaveIcon />}
                onClick={handleSaveSaml}
                disabled={saving}
                sx={primaryButtonSx}
              >
                {saving ? "Saving…" : "Save SAML Settings"}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SSOSettings;
